// BrowserKeysPanel — the owner's window onto the keys their app's UI READS, and where they
// paste each value.
//
// ── HOW IT DIFFERS FROM SecretsPanel, AND WHY ───────────────────────────────
//
// A secret's value never reaches the app, so that panel never shows one. A browser key is the
// opposite by construction: it is compiled into the JavaScript every visitor downloads
// ("Publishable API key `pk_…` — safe to expose: Yes", Stripe's own table). So:
//
//   • the value is SHOWN, in plain text, in a plain text field — hiding it would only stop the
//     owner checking they pasted the publishable one and not its secret twin, which is the one
//     mistake that matters here;
//   • every row carries the vendor's WARNING, because what protects a key in a public bundle
//     is the restriction the owner sets at the vendor — nothing the platform can do for them;
//   • saving says the key is not live until the next deploy, because it is compiled in at
//     BUILD time and an owner who reloads and sees the same blank map concludes the platform
//     is broken.
//
// Which keys appear is decided by the app's own SOURCE (what it reads), not by its package
// list: `react-map-gl` needs a Mapbox token on one import path and none on the other.
//
// Every decision — validation, ordering, wording — lives in lib/browser_keys_view and is unit
// tested there; this file is the markup.
import { useState } from "react";
import { useBrowserKeys } from "../hooks/useBrowserKeys";
import {
  afterSaveNote, browserKeyStatusLabel, emptyStateText, sortBrowserKeys, unsetCount, validateBrowserKeyValue, vendorHost } from "../lib/browser_keys_view";
import type { BrowserKeyRow, SecretsEnv } from "../api";

export default function BrowserKeysPanel({ slug, initialEnv = "dev" }: { slug: string; initialEnv?: SecretsEnv }) {
  const [env, setEnv] = useState<SecretsEnv>(initialEnv);
  const k = useBrowserKeys(slug, env);
  const rows = k.keys ?? [];

  return (
    <div className="flex h-full w-full flex-col bg-bg">
      {/* ONE THIN ROW.
          Gone: the "Keys in your app" heading (the tab says it), the paragraph describing what these
          keys are, and "used while the app is being built" under `dev`, which described the obvious.

          KEPT, in six words: "public in your app — restrict at the provider". Not a description — these
          keys ship inside the app's code where anyone can read them, and it is the one thing on this
          screen that can cost the operator money if nobody tells them. Shortened, not deleted.

          KEPT too: the count of keys still to set, which is STATE rather than prose, and the prod
          warning, which is the other thing that bites. */}
      <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-2">
        <div role="tablist" aria-label="Environment" className="flex min-w-0 items-center gap-1">
          {(["dev", "prod"] as const).map((e) => (
            <button key={e} type="button" role="tab" aria-selected={env === e} onClick={() => setEnv(e)}
              className={["rounded-md px-2.5 py-1 text-[11px] transition", env === e ? "bg-accent text-white" : "text-text-secondary hover:bg-surface-muted hover:text-text"].join(" ")}>
              {e}
            </button>
          ))}
          {k.keys !== null && unsetCount(rows) > 0 && (
            <span className="shrink-0 pl-2 text-[11px] text-text-tertiary">{unsetCount(rows)} to set</span>
          )}
          <span className="truncate pl-2 text-[11px] text-text-tertiary">public in your app — restrict at the provider</span>
          {env === "prod" && (
            <span className="truncate pl-2 text-[11px] text-warn dark:text-warn">set these before publishing</span>
          )}
        </div>
        <button type="button" onClick={k.reload} aria-label="Refresh" title="Refresh" className={ICON_BTN}><RefreshIcon /></button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
        {k.error
          ? <ErrorNote text={k.error} />
          : k.keys === null
            ? <Muted>Loading keys…</Muted>
            : rows.length === 0
              ? <Muted>{emptyStateText(rows)}</Muted>
              // The same grid as the secrets panel, for the same reason: a centred column capped at 42rem
              // left a wide panel empty on both sides and made the operator scroll past it.
              : <ul className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(24rem,1fr))]">
                  {sortBrowserKeys(rows).map((row) => (
                    <KeyRow key={row.env} row={row} env={env} onSet={k.set} onClear={k.clear} />
                  ))}
                </ul>}
      </div>
    </div>
  );
}

function KeyRow({ row, env, onSet, onClear }: {
  row: BrowserKeyRow;
  env: SecretsEnv;
  onSet: (name: string, value: string) => Promise<void>;
  onClear: (name: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  const [saved, setSaved]     = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const reset = () => { setEditing(false); setValue(""); setErr(null); setConfirmClear(false); };

  const save = async () => {
    // The same rule the server enforces, applied to THIS row's own shapes (the row carries
    // them, so there is no second table here to drift). The server judges again on write.
    const check = validateBrowserKeyValue(row, value);
    if (check.ok === false) { setErr(check.error); return; }
    setBusy(true); setErr(null);
    try { await onSet(row.env, check.value); reset(); setSaved(afterSaveNote(env)); }
    catch (e) { setErr(String((e as Error)?.message ?? e)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true); setErr(null);
    try { await onClear(row.env); reset(); setSaved(null); }
    catch (e) { setErr(String((e as Error)?.message ?? e)); setConfirmClear(false); }
    finally { setBusy(false); }
  };

  return (
    <li className="rounded-xl border border-border bg-surface px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text">{row.vendor}</span>
            <StatusBadge isSet={row.isSet} />
          </div>
          <p className="mt-1 text-xs text-text-secondary">To {row.purpose}.</p>
          <p className="mt-1 text-[11px] text-text-tertiary">{browserKeyStatusLabel(row)}</p>
          {/* The key is public; what protects it is the restriction, and only the owner can
              set that. So the warning is not a tooltip — it is on the row, always. */}
          <p className="mt-2 rounded-lg bg-surface-muted px-3 py-2 text-[11px] leading-relaxed text-text-secondary">{row.warning}</p>
          <p className="mt-1.5 text-[11px] text-text-tertiary">
            Get it at <a href={row.where} target="_blank" rel="noreferrer noopener" className="text-accent underline">{vendorHost(row.where)}</a>
            {row.packages.length > 0 && <> · asked for by <span className="font-mono">{row.packages.join(", ")}</span></>}
          </p>
        </div>
        {!editing && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button type="button" onClick={() => { setEditing(true); setValue(row.value ?? ""); setSaved(null); }} className={row.isSet ? BTN_GHOST : BTN_PRIMARY}>
              {row.isSet ? "Replace" : "Set key"}
            </button>
            {row.isSet && !confirmClear && <button type="button" onClick={() => setConfirmClear(true)} className={BTN_GHOST}>Clear</button>}
          </div>
        )}
      </div>

      {/* The value in plain sight when set: it is in the app's code anyway, and an owner who
          cannot see it cannot tell a publishable key from its secret twin. */}
      {row.isSet && !editing && row.value && (
        <p className="mt-2 break-all font-mono text-[11px] text-text-secondary">{row.value}</p>
      )}

      {saved && !editing && <p className="mt-2 text-[11px] text-text-secondary">{saved}</p>}

      {confirmClear && !editing && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-surface-muted px-3 py-2 text-xs">
          <span className="text-text-secondary">Clear this key? That part of your app renders nothing until it is set again.</span>
          <button type="button" className={BTN_DANGER} onClick={clear} disabled={busy}>{busy ? "Clearing…" : "Clear"}</button>
          <button type="button" className={BTN_GHOST} onClick={() => setConfirmClear(false)} disabled={busy}>Cancel</button>
        </div>
      )}

      {editing && (
        <div className="mt-3">
          {/* A PLAIN TEXT field, deliberately: this is not a password, and masking it would
              hide the one error that matters — a secret key pasted where a publishable one
              belongs. `autoComplete="off"` keeps a password manager from treating it as a
              login, and a unique name keeps two rows from being "the same field" to one. */}
          <input
            type="text"
            name={`browser-key-${row.env}`}
            autoComplete="off"
            data-1p-ignore data-lpignore="true" data-bwignore="true" data-form-type="other"
            value={value}
            autoFocus
            onChange={(e) => { setValue(e.target.value); if (err) setErr(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") reset(); }}
            placeholder={row.mustStartWith.length > 0 ? `Paste the key that starts with ${row.mustStartWith[0]}` : "Paste the key"}
            className="w-full min-w-0 rounded-lg border border-border bg-bg px-3 py-1.5 font-mono text-xs text-text outline-none focus:border-border-active"
          />
          <p className="mt-1.5 font-mono text-[11px] text-text-tertiary">{row.env}</p>
          <div className="mt-2 flex items-center gap-1.5">
            <button type="button" className={BTN_PRIMARY} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
            <button type="button" className={BTN_GHOST} onClick={reset} disabled={busy}>Cancel</button>
          </div>
          {err && <p className="mt-2 text-[11px] leading-relaxed text-danger">{err}</p>}
        </div>
      )}
    </li>
  );
}

function StatusBadge({ isSet }: { isSet: boolean }) {
  return (
    <span className={["rounded-full px-2 py-0.5 text-[10px] font-medium", isSet ? "bg-ok-soft text-ok" : "bg-warn-soft text-warn"].join(" ")}>
      {isSet ? "set" : "needed"}
    </span>
  );
}
function Muted({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-md py-10 text-center text-sm leading-relaxed text-text-secondary">{children}</div>;
}
function ErrorNote({ text }: { text: string }) {
  return (
    <div className="mx-auto max-w-md py-10 text-center">
      <p className="text-sm font-medium text-text">Couldn’t load this app’s keys</p>
      <p className="mt-1.5 text-xs leading-relaxed text-text-secondary">{text}</p>
    </div>
  );
}

const ICON_BTN    = "rounded-md p-1.5 text-text-secondary transition hover:bg-surface-muted hover:text-text";
const BTN_PRIMARY = "rounded-full bg-accent px-3 py-1 text-xs font-medium text-white transition hover:bg-accent-hover disabled:opacity-50";
const BTN_GHOST   = "rounded-full border border-border px-3 py-1 text-xs text-text-secondary transition hover:border-border-active hover:text-text disabled:opacity-50";
const BTN_DANGER  = "rounded-full bg-danger-strong px-3 py-1 text-xs font-medium text-white transition hover:bg-danger-hover disabled:opacity-50";

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}
